import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../components/SEOHead';

const { 
  FiPlay, FiShield, FiGlobe, FiMessageCircle, FiInfinityIcon, FiUser, FiX, FiChevronDown, 
  FiAlertTriangle, FiDollarSign, FiClock, FiZap, FiTrendingDown, FiPlus, FiMinus,
  FiSettings, FiBulb, FiSearch, FiFileText, FiPalette, FiCode, FiMegaphone, 
  FiCpu, FiRocket, FiGift, FiWave, FiStar, FiAward, FiUsers, FiHeart, FiTarget,
  FiCheckCircle, FiTrendingUp, FiBookOpen, FiMic, FiCheck, FiEye, FiArrowDown,
  FiThumbsUp, FiRefreshCw, FiUpload
} = FiIcons;

const AWXLandingPage = () => {
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [currentShimmerChip, setCurrentShimmerChip] = useState(0);
  const [expandedProblem, setExpandedProblem] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [currentModuleShimmer, setCurrentModuleShimmer] = useState(0);
  const [expandedTeamMember, setExpandedTeamMember] = useState(null);
  const [currentInstructorShimmer, setCurrentInstructorShimmer] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [currentWaveShimmer, setCurrentWaveShimmer] = useState(0);
  const [isRibbonPaused, setIsRibbonPaused] = useState(false);

  // Animated counters for stats
  const [appsBuilt, setAppsBuilt] = useState(0);
  const [studentsGuided, setStudentsGuided] = useState(0);
  const [yearsTeaching, setYearsTeaching] = useState(0);

  // Shimmer animation for trust chips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentShimmerChip((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Shimmer animation for module headings
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentModuleShimmer((prev) => (prev + 1) % 11);
    }, 12000);
    return () => clearInterval(interval);
  }, []);

  // Shimmer animation for instructor stats
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInstructorShimmer((prev) => (prev + 1) % 3);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Wave animation for sneak peek section
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWaveShimmer((prev) => (prev + 1) % 2);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Animated counter effect
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const appsTarget = 56;
      const studentsTarget = 540;
      const yearsTarget = 3.5;
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        setAppsBuilt(Math.floor(appsTarget * easeOut));
        setStudentsGuided(Math.floor(studentsTarget * easeOut));
        setYearsTeaching(parseFloat((yearsTarget * easeOut).toFixed(1)));
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setAppsBuilt(appsTarget);
          setStudentsGuided(studentsTarget);
          setYearsTeaching(yearsTarget);
        }
      }, duration / steps);
    };

    // Trigger animation when component mounts
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    const instructorSection = document.getElementById('instructor');
    if (instructorSection) {
      observer.observe(instructorSection);
    }

    return () => observer.disconnect();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isTrailerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isTrailerOpen]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsTrailerOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const trustChips = [
    { icon: FiShield, text: '60-Day Guarantee' },
    { icon: FiGlobe, text: 'English + हिंदी' },
    { icon: FiMessageCircle, text: 'Live Group Q&A' },
    { icon: FiInfinityIcon, text: 'Lifetime Access & Updates' },
    { icon: FiUser, text: 'Beginner-friendly (No prior coding needed)' }
  ];

  const problems = [
    {
      id: 1,
      icon: FiAlertTriangle,
      title: "Tutorials that look perfect… until they break on your laptop.",
      consequence: "Wasted 6 months. Still no app live.",
      keywords: ["break", "errors"]
    },
    {
      id: 2,
      icon: FiZap,
      title: "AI giving you hallucinated code you can't debug.",
      consequence: "Stuck with mysterious errors that Google can't solve.",
      keywords: ["hallucinated", "debug"]
    },
    {
      id: 3,
      icon: FiClock,
      title: "Endless Googling errors with no answers.",
      consequence: "Nights wasted on Stack Overflow with no progress.",
      keywords: ["Googling", "errors", "wasted time"]
    },
    {
      id: 4,
      icon: FiDollarSign,
      title: "You're paying EMIs, not building assets.",
      consequence: "₹50,000+ spent on courses. Zero deployed projects.",
      keywords: ["EMIs", "paying"]
    },
    {
      id: 5,
      icon: FiTrendingDown,
      title: "No clear roadmap — just confusion between frameworks, databases, and deployment.",
      consequence: "Analysis paralysis. Every choice feels wrong.",
      keywords: ["confusion", "roadmap"]
    }
  ];

  const modules = [
    {
      id: 1,
      icon: FiWave,
      emoji: "👋",
      title: "Getting to Know You",
      description: "Set up your dev environment, align goals, and get matched with the right starter project.",
      outcome: "You stop feeling overwhelmed and start with a personalized app path.",
      image: "setup-environment"
    },
    {
      id: 2,
      icon: FiSettings,
      emoji: "⚙️",
      title: "Basics",
      description: "Master the AI-assisted dev stack: V0 for UI, Supabase for backend, Razorpay for payments.",
      outcome: "You know your tools. No more wasting time googling \"best framework.\"",
      image: "tech-stack"
    },
    {
      id: 3,
      icon: FiBulb,
      emoji: "💡",
      title: "App Concept",
      description: "Choose a real app idea (marketplace, ordering system, or ROI calculator) and scope it in one page.",
      outcome: "A crystal-clear project spec — not a fuzzy idea.",
      image: "app-concept"
    },
    {
      id: 4,
      icon: FiSearch,
      emoji: "🔍",
      title: "Validation",
      description: "Run market-style validation with AI + research workflows.",
      outcome: "You know if your idea has demand before you waste time building it.",
      image: "market-validation"
    },
    {
      id: 5,
      icon: FiFileText,
      emoji: "🗂️",
      title: "Wireframing",
      description: "Create clickable flows with AI + Figma in hours, not weeks.",
      outcome: "Your app's blueprint is visible and testable.",
      image: "wireframes"
    },
    {
      id: 6,
      icon: FiPalette,
      emoji: "🎨",
      title: "Design Briefs",
      description: "Translate wireframes into actionable design specs that a designer or AI can implement.",
      outcome: "You never struggle to hand off or design again.",
      image: "design-system"
    },
    {
      id: 7,
      icon: FiCode,
      emoji: "💻",
      title: "Development",
      description: "Build frontend, backend, and auth live. Fix errors together in weekly calls.",
      outcome: "Your first working app — deployed with payments, auth, and dashboards.",
      image: "live-coding"
    },
    {
      id: 8,
      icon: FiMegaphone,
      emoji: "📢",
      title: "Marketing Content",
      description: "Generate landing pages, funnels, and assets to position your app.",
      outcome: "You have not just an app, but a way to show it off.",
      image: "marketing-assets"
    },
    {
      id: 9,
      icon: FiCpu,
      emoji: "🤖",
      title: "Automation",
      description: "Use AI to streamline workflows (emails, notifications, workflows).",
      outcome: "Your app runs without manual babysitting.",
      image: "automation"
    },
    {
      id: 10,
      icon: FiRocket,
      emoji: "🚀",
      title: "Final Thoughts",
      description: "Polish, prep your portfolio, and publish your live link.",
      outcome: "A public URL you can share with recruiters, clients, or employers.",
      image: "deployment"
    },
    {
      id: 11,
      icon: FiGift,
      emoji: "🎁",
      title: "Bonus",
      description: "Advanced case studies and tricks: from coupon funnels to client onboarding flows.",
      outcome: "Insider edge that shortcuts months of trial and error.",
      image: "bonus-content"
    }
  ];

  const teamMembers = [
    {
      id: 1,
      name: "Aishwarya",
      role: "Community Success",
      emoji: "🤝",
      icon: FiUsers,
      description: "Runs weekly peer syncs, helps you stay accountable, and ensures no question goes unanswered.",
      expandedBio: "With 2+ years in community building, Aishwarya creates the support system that keeps you motivated. She personally tracks progress and celebrates every milestone with you."
    },
    {
      id: 2,
      name: "Nihal",
      role: "AI Workflow Specialist",
      emoji: "🔧",
      icon: FiCpu,
      description: "Behind the scenes building systems that power real client apps — the same methods you'll learn in this course.",
      expandedBio: "Former startup CTO who built 3 AI products from scratch. Now he breaks down complex workflows into simple, repeatable processes for students."
    },
    {
      id: 3,
      name: "Priya",
      role: "Design & UX Mentor",
      emoji: "🎨",
      icon: FiPalette,
      description: "Turns your wireframes into beautiful, conversion-focused designs that users actually want to use.",
      expandedBio: "Ex-Google designer with 50+ app designs. She runs bonus wireframing sessions and helps you think like a user, not just a developer."
    },
    {
      id: 4,
      name: "Guest Mentors",
      role: "Industry Experts",
      emoji: "⭐",
      icon: FiStar,
      description: "Rotating cast of founders, senior devs, and product managers sharing real-world insights.",
      expandedBio: "Monthly sessions with startup founders who've raised funding, senior engineers from top companies, and product managers who've launched successful apps."
    }
  ];

  const trailerHighlights = [
    {
      icon: FiRocket,
      text: "How we go from idea → app in weeks"
    },
    {
      icon: FiCode,
      text: "Real examples like Annapurna Foods & Kushl.app"
    },
    {
      icon: FiUsers,
      text: "The live, in-the-trenches teaching style"
    },
    {
      icon: FiTarget,
      text: "What you'll ship in just 2–6 weeks"
    }
  ];

  const promisePerks = [
    {
      icon: FiInfinityIcon,
      title: "Lifetime Access",
      description: "Revisit modules anytime, at your pace."
    },
    {
      icon: FiShield,
      title: "60-Day Guarantee",
      description: "If we don't deliver, you get 100% refund."
    },
    {
      icon: FiRefreshCw,
      title: "Free Upgrades",
      description: "All future updates included, no extra fees."
    },
    {
      icon: FiMessageCircle,
      title: "Live Group Q&As",
      description: "Weekly sessions, so you're never stuck alone."
    }
  ];

  const ribbonBenefits = [
    "Live group Q&A sessions",
    "Virtual peer community",
    "Free access to upgrades",
    "Lifetime course access",
    "Refund guarantee",
    "Weekly live debugging",
    "English & हिंदी support",
    "Real app deployments",
    "Personal mentorship",
    "Industry expert sessions"
  ];

  const moduleImages = {
    "setup-environment": "💻 Environment Setup",
    "tech-stack": "🛠️ V0 + Supabase + Razorpay",
    "app-concept": "💡 Project Specification",
    "market-validation": "📊 Market Research",
    "wireframes": "📱 Figma Wireframes",
    "design-system": "🎨 Design Handoff",
    "live-coding": "⚡ Live Development",
    "marketing-assets": "📈 Landing Pages",
    "automation": "🤖 AI Workflows",
    "deployment": "🌍 Live Portfolio",
    "bonus-content": "🎯 Advanced Strategies"
  };

  const scrollToCurriculum = () => {
    const curriculumSection = document.getElementById('curriculum');
    if (curriculumSection) {
      curriculumSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToInstructor = () => {
    const instructorSection = document.getElementById('instructor');
    if (instructorSection) {
      instructorSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTestimonials = () => {
    const testimonialsSection = document.getElementById('testimonials');
    if (testimonialsSection) {
      testimonialsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPromise = () => {
    const promiseSection = document.getElementById('promise');
    if (promiseSection) {
      promiseSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const highlightKeywords = (text, keywords) => {
    let highlightedText = text;
    keywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<span class="text-red-500 line-through">$1</span>');
    });
    return highlightedText;
  };

  const handleModuleClick = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
    
    // Auto-scroll to center the expanded module on mobile
    if (expandedModule !== moduleId) {
      setTimeout(() => {
        const moduleElement = document.getElementById(`module-${moduleId}`);
        if (moduleElement) {
          moduleElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
  };

  const handleTeamMemberClick = (memberId) => {
    setExpandedTeamMember(expandedTeamMember === memberId ? null : memberId);
  };

  const handleLanguageToggle = (language) => {
    setSelectedLanguage(language);
  };

  return (
    <>
      <SEOHead 
        title="Ship Real AI Apps Fast - AWX Course | ItsKaivalya"
        description="Join weekly live debugging sessions. Build with V0 + Supabase + Razorpay. Deploy production AI apps, marketplaces, and dashboards. English & हिंदी support."
        canonicalUrl="https://lp.itskaivalya.com/develop-apps-with-ai-awx"
        noIndex={true}
      />

      <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50">
        {/* Non-sticky Header */}
        <header className="px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <div className="h-6 w-6 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold">
              AWX
            </div>
            <span className="ml-2 text-sm font-semibold">AI Course</span>
          </div>
          <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center text-xs font-semibold">
            ItsKaivalya
          </div>
        </header>

        {/* Hero Section - Mobile First */}
        <section className="relative min-h-[calc(100vh-80px)] flex flex-col justify-center px-4 pb-8">
          <div className="max-w-md mx-auto lg:max-w-6xl lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            
            {/* Left Column - Copy */}
            <div className="text-center lg:text-left">
              {/* Micro-header */}
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-4 font-medium">
                Weekly live debugging • Real deployments • English & हिंदी
              </div>

              {/* Headline */}
              <h1 className="text-3xl lg:text-5xl font-extrabold leading-tight mb-4 text-slate-900 dark:text-slate-50">
                Ship real AI apps in weeks — not months.
              </h1>

              {/* Subheadline */}
              <p className="text-base lg:text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                Join weekly live sessions where we fix your errors on screen, use a simple stack (V0 + Supabase + Razorpay), and deploy real marketplaces, tools, and dashboards you can show to clients and recruiters.
              </p>

              {/* Trust Chips */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-8">
                {trustChips.map((chip, index) => (
                  <motion.div
                    key={index}
                    className={`relative overflow-hidden inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium border ${
                      currentShimmerChip === index
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    {currentShimmerChip === index && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ duration: 1.5, ease: 'linear' }}
                      />
                    )}
                    <SafeIcon icon={chip.icon} className="w-3 h-3" />
                    <span>{chip.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                <motion.button
                  onClick={() => setIsTrailerOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiPlay} className="w-5 h-5" />
                  Watch 60-sec trailer
                </motion.button>
                
                <motion.button
                  onClick={scrollToCurriculum}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-semibold rounded-lg transition-all duration-200"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  View Curriculum
                </motion.button>
              </div>

              <div className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                See the roadmap & sample apps
              </div>

              {/* Social Proof Line */}
              <div className="text-sm text-slate-600 dark:text-slate-400 text-center lg:text-left">
                "Landed 3 clients in 6 weeks." • "2× salary after 1 project." • 500+ learners
              </div>
            </div>

            {/* Right Column - Trailer Visual */}
            <div className="mt-12 lg:mt-0 flex justify-center">
              <motion.div
                className="relative w-80 h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-2xl shadow-2xl flex items-center justify-center cursor-pointer overflow-hidden"
                onClick={() => setIsTrailerOpen(true)}
                whileHover={{ scale: 1.02, rotateY: 1.5 }}
                transition={{ duration: 0.3 }}
              >
                {/* Code Editor Mockup */}
                <div className="w-72 h-72 bg-slate-900 dark:bg-slate-800 rounded-lg p-4 relative">
                  {/* Editor Header */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="ml-auto text-xs text-slate-400">app.js</div>
                  </div>

                  {/* Code Lines */}
                  <div className="space-y-2 text-xs font-mono">
                    <div className="text-slate-300">const app = createApp()</div>
                    <motion.div 
                      className="text-red-400"
                      animate={{ color: ['#f87171', '#10b981'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
                    >
                      app.use(aiMiddleware)
                    </motion.div>
                    <div className="text-slate-300">app.deploy()</div>
                  </div>

                  {/* Deployed Toast */}
                  <motion.div
                    className="absolute bottom-4 right-4 bg-green-500 text-white px-3 py-1 rounded text-xs font-medium"
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1, duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
                  >
                    ✅ Deployed
                  </motion.div>
                </div>

                {/* Play Button Overlay */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ scale: [0.97, 1.03, 0.97] }}
                  transition={{ duration: 1.2, ease: 'easeInOut', repeat: Infinity }}
                >
                  <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                    <SafeIcon icon={FiPlay} className="w-8 h-8 text-blue-500 ml-1" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
          <div className="max-w-4xl mx-auto">
            {/* Section Title */}
            <motion.h2 
              className="text-3xl lg:text-4xl font-extrabold text-center mb-4 text-slate-900 dark:text-slate-50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Are You Struggling With These Problems?
            </motion.h2>

            {/* Animated Sliders */}
            <div className="mb-16 overflow-hidden">
              {/* Top Slider - Moving Right */}
              <motion.div
                className="flex gap-8 mb-4"
                animate={{ x: ['-100%', '0%'] }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              >
                <div className="flex gap-8 whitespace-nowrap">
                  <span className="text-red-500 font-medium">Tutorial Hell</span>
                  <span className="text-red-500 font-medium">AI Lies</span>
                  <span className="text-red-500 font-medium">Broken Code</span>
                  <span className="text-red-500 font-medium">Wasted Time</span>
                  <span className="text-red-500 font-medium">No Progress</span>
                  <span className="text-red-500 font-medium">Endless Errors</span>
                  <span className="text-red-500 font-medium">Tutorial Hell</span>
                  <span className="text-red-500 font-medium">AI Lies</span>
                  <span className="text-red-500 font-medium">Broken Code</span>
                </div>
              </motion.div>

              {/* Bottom Slider - Moving Left */}
              <motion.div
                className="flex gap-8"
                animate={{ x: ['0%', '-100%'] }}
                transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
              >
                <div className="flex gap-8 whitespace-nowrap">
                  <span className="text-orange-500 font-medium">₹50K+ Wasted</span>
                  <span className="text-orange-500 font-medium">Zero Deployments</span>
                  <span className="text-orange-500 font-medium">Analysis Paralysis</span>
                  <span className="text-orange-500 font-medium">EMI Trap</span>
                  <span className="text-orange-500 font-medium">No Clear Path</span>
                  <span className="text-orange-500 font-medium">Confusion</span>
                  <span className="text-orange-500 font-medium">₹50K+ Wasted</span>
                  <span className="text-orange-500 font-medium">Zero Deployments</span>
                  <span className="text-orange-500 font-medium">Analysis Paralysis</span>
                </div>
              </motion.div>
            </div>

            {/* Problem Cards */}
            <div className="space-y-6">
              {problems.map((problem, index) => (
                <motion.div
                  key={problem.id}
                  className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-xl transition-all duration-300 hover:border-red-200 dark:hover:border-red-800"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => setExpandedProblem(expandedProblem === problem.id ? null : problem.id)}
                  whileHover={{ scale: 1.02, backgroundColor: 'rgba(248, 113, 113, 0.05)' }}
                >
                  <div className="flex items-start gap-4">
                    {/* Animated Icon */}
                    <motion.div
                      className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center"
                      animate={
                        problem.icon === FiDollarSign 
                          ? { y: [0, -3, 0] }
                          : problem.icon === FiAlertTriangle
                          ? { rotate: [0, -5, 5, 0] }
                          : {}
                      }
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <SafeIcon icon={problem.icon} className="w-6 h-6 text-red-500" />
                    </motion.div>

                    <div className="flex-1">
                      <h3 
                        className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2"
                        dangerouslySetInnerHTML={{ 
                          __html: highlightKeywords(problem.title, problem.keywords) 
                        }}
                      />
                      
                      <AnimatePresence>
                        {expandedProblem === problem.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <p className="text-red-600 dark:text-red-400 font-medium bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                              {problem.consequence}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <SafeIcon 
                      icon={FiChevronDown} 
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${
                        expandedProblem === problem.id ? 'rotate-180' : ''
                      }`} 
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Animated Balance Scale Visual */}
            <div className="flex justify-center my-16">
              <motion.div
                className="relative w-32 h-32"
                animate={{ rotate: [-2, 2, -2] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="absolute inset-0 bg-slate-300 dark:bg-slate-600 rounded-full flex items-center justify-center">
                  <div className="text-2xl">⚖️</div>
                </div>
                {/* Falling Rupee Coins */}
                <motion.div
                  className="absolute -top-8 left-4 text-2xl"
                  animate={{ y: [0, 40, 0], opacity: [1, 0, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                >
                  ₹
                </motion.div>
                <motion.div
                  className="absolute -top-8 right-4 text-2xl"
                  animate={{ y: [0, 40, 0], opacity: [1, 0, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  ₹
                </motion.div>
                <motion.div
                  className="absolute -top-8 left-8 text-2xl"
                  animate={{ y: [0, 40, 0], opacity: [1, 0, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                >
                  ₹
                </motion.div>
              </motion.div>
            </div>

            {/* Bridge to Solution */}
            <motion.div
              className="text-center mt-16 p-8 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-2xl border border-green-200 dark:border-green-800"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                <span className="text-slate-800 dark:text-slate-200">It doesn't have to stay this way.</span>
                <br />
                <span className="text-green-600 dark:text-green-400">
                  You can learn to build, deploy, and scale AI apps — the right way.
                </span>
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                No more broken tutorials. No more AI lies. Just real apps that work.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Course Curriculum Section */}
        <section id="curriculum" className="py-20 px-4 bg-white dark:bg-slate-900">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl font-extrabold mb-4 text-slate-900 dark:text-slate-50">
                Course Curriculum
              </h2>
              <p className="text-lg lg:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                Step-by-step. Zero guesswork. By the end, you'll have live apps deployed.
              </p>
            </motion.div>

            {/* Desktop Layout: Two Columns */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-16">
              
              {/* Left Column: Module Accordions */}
              <div className="space-y-4">
                {modules.map((module, index) => (
                  <motion.div
                    key={module.id}
                    id={`module-${module.id}`}
                    className={`relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      expandedModule === module.id
                        ? 'border-violet-400 dark:border-violet-500 shadow-lg shadow-violet-100 dark:shadow-violet-900/20 bg-violet-50/50 dark:bg-violet-900/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => handleModuleClick(module.id)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {/* Shimmer Effect */}
                    {currentModuleShimmer === index && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-200/20 dark:via-violet-400/20 to-transparent"
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ duration: 2, ease: 'linear' }}
                      />
                    )}

                    <div className="p-6">
                      {/* Module Header */}
                      <div className="flex items-start gap-4">
                        {/* Icon + Emoji */}
                        <div className="flex-shrink-0 w-12 h-12 bg-violet-100 dark:bg-violet-900/20 rounded-lg flex items-center justify-center relative">
                          <span className="text-xl">{module.emoji}</span>
                          <SafeIcon 
                            icon={module.icon} 
                            className="absolute -bottom-1 -right-1 w-4 h-4 text-violet-500 bg-white dark:bg-slate-800 rounded-full p-0.5" 
                          />
                        </div>

                        <div className="flex-1">
                          {/* Module Title with underline */}
                          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                            <span className="border-b-2 border-violet-400 pb-1">
                              Module {module.id}: {module.title}
                            </span>
                          </h3>
                          
                          {/* Short Description - Always Visible */}
                          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                            {module.description}
                          </p>
                        </div>

                        {/* Expand/Collapse Icon */}
                        <div className="flex-shrink-0">
                          <SafeIcon 
                            icon={expandedModule === module.id ? FiMinus : FiPlus}
                            className={`w-5 h-5 transition-all duration-300 ${
                              expandedModule === module.id 
                                ? 'text-violet-500 rotate-180' 
                                : 'text-slate-400'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <AnimatePresence>
                        {expandedModule === module.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700"
                          >
                            {/* Outcome */}
                            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                              <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                                <span>👉</span> Outcome:
                              </h4>
                              <p className="text-green-700 dark:text-green-300 font-medium">
                                {module.outcome}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Right Column: Static Illustration (Desktop Only) */}
              <div className="hidden lg:block lg:sticky lg:top-8 lg:h-fit">
                <motion.div
                  className="bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-8 h-96 flex items-center justify-center shadow-xl"
                  key={expandedModule}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-center">
                    <div className="text-6xl mb-4">
                      {expandedModule 
                        ? moduleImages[modules.find(m => m.id === expandedModule)?.image] 
                        : "🚀 Choose a Module"}
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 font-medium">
                      {expandedModule 
                        ? `Module ${expandedModule} Visual`
                        : "Click any module to see details"}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* End Transition */}
            <motion.div
              className="text-center mt-16 p-8 bg-gradient-to-r from-green-50 to-violet-50 dark:from-green-900/20 dark:to-violet-900/20 rounded-2xl border border-green-200 dark:border-green-800"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-green-600 dark:text-green-400 mb-4">
                By Week 6: You'll have your first deployed app live — payments, dashboards, and all.
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                No more "someday I'll build something." You'll have proof.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Instructor Bio & Team Section */}
        <section id="instructor" className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg: font-extrabold mb-4">
                Meet Your Instructor
              </h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                In the trenches with you. Every. Single. Week.
              </p>
            </motion.div>

            {/* Instructor Profile - Desktop Two Column */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center mb-20">
              
              {/* Left Column: Photo (Desktop) / Top (Mobile) */}
              <motion.div
                className="mb-8 lg:mb-0 flex justify-center lg:justify-end"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative">
                  {/* Instructor Photo Placeholder */}
                  <motion.div
                    className="w-80 h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-violet-600 to-blue-600 rounded-2xl flex items-center justify-center text-8xl lg:text-9xl shadow-2xl relative overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Instructor Avatar */}
                    <div className="text-white/90 z-10">👨‍💻</div>
                    
                    {/* Name overlay */}
                    <div className="absolute bottom-6 left-6 z-10">
                      <h3 className="text-2xl font-bold text-white">Kaivalya</h3>
                      <p className="text-violet-200">AI Development Instructor</p>
                    </div>

                    {/* Subtle border glow */}
                    <div className="absolute inset-0 rounded-2xl border-2 border-violet-400/30"></div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right Column: Bio & Stats */}
              <motion.div
                className="text-center lg:text-left"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Name & Title */}
                <h3 className="text-3xl lg:text-4xl font-extrabold mb-2">
                  Kaivalya Deshpande
                </h3>
                <p className="text-xl text-violet-300 mb-8">
                  AI Development Instructor & Builder
                </p>

                {/* Bio Text */}
                <div className="space-y-4 text-slate-300 leading-relaxed mb-8">
                  <p>
                    I've built and shipped <strong className="text-white">{appsBuilt}+ AI-powered applications</strong> — from Zomato-style ordering systems with real payments, to marketplaces handling thousands of users, to business tools generating real revenue.
                  </p>
                  <p>
                    But more importantly — <strong className="text-violet-300">I've been exactly where you are.</strong> 
                    Stuck in tutorial hell. Copy–pasting code that didn't work. Wondering if I'd ever ship anything real.
                  </p>
                  <p>
                    That's why I built this program. Not to show off. Not to give you slides. But to give you the <strong className="text-white">exact system I wish I had when I started.</strong>
                  </p>
                  <p className="text-lg font-medium text-white">
                    And I'll be with you live, every week, fixing errors and making sure you actually ship.
                  </p>
                </div>

                {/* Stats Row with Animated Counters */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                  {[
                    { 
                      value: `${appsBuilt}+`, 
                      label: 'Apps Built & Deployed',
                      icon: FiRocket,
                      shimmer: currentInstructorShimmer === 0
                    },
                    { 
                      value: `${studentsGuided}+`, 
                      label: 'Students Guided',
                      icon: FiUsers,
                      shimmer: currentInstructorShimmer === 1
                    },
                    { 
                      value: `${yearsTeaching}`, 
                      label: 'Years Teaching AI Development',
                      icon: FiBookOpen,
                      shimmer: currentInstructorShimmer === 2
                    }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className={`relative overflow-hidden bg-slate-800 rounded-xl p-6 border-2 transition-all duration-300 ${
                        stat.shimmer
                          ? 'border-violet-400 bg-violet-900/20'
                          : 'border-slate-700'
                      }`}
                      whileHover={{ scale: 1.05, borderColor: '#8b5cf6' }}
                    >
                      {/* Shimmer Effect */}
                      {stat.shimmer && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-400/20 to-transparent"
                          initial={{ x: '-100%' }}
                          animate={{ x: '100%' }}
                          transition={{ duration: 2, ease: 'linear' }}
                        />
                      )}

                      <div className="relative z-10">
                        <SafeIcon icon={stat.icon} className="w-8 h-8 text-violet-400 mb-3 mx-auto sm:mx-0" />
                        <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                        <div className="text-sm text-slate-300">{stat.label}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quote Box */}
                <motion.div
                  className="bg-gradient-to-r from-violet-900/50 to-blue-900/50 rounded-xl p-6 border border-violet-400/30"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <SafeIcon icon={FiMic} className="w-6 h-6 text-violet-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-lg font-medium text-white italic leading-relaxed">
                        "My promise: I'll be in the call with you — debugging your code, answering your questions, and pushing you until you launch."
                      </p>
                      <div className="flex items-center gap-2 mt-4">
                        <div className="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center text-sm">
                          K
                        </div>
                        <span className="text-violet-300 font-medium">Kaivalya Deshpande</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Team Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Team Header */}
              <div className="text-center mb-12">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                  Meet the Maitreya Labs Team
                </h3>
                <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                  Your success isn't just my responsibility — it's our entire team's mission.
                </p>
              </div>

              {/* Team Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-violet-400/50 transition-all duration-300 cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => handleTeamMemberClick(member.id)}
                    whileHover={{ scale: 1.02, y: -5 }}
                  >
                    {/* Member Header */}
                    <div className="text-center mb-4">
                      {/* Avatar Circle */}
                      <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 mx-auto">
                        {member.emoji}
                      </div>
                      
                      {/* Name & Role */}
                      <h4 className="text-lg font-bold text-white mb-1">{member.name}</h4>
                      <p className="text-violet-300 text-sm font-medium mb-3">{member.role}</p>
                      
                      {/* Icon */}
                      <SafeIcon icon={member.icon} className="w-5 h-5 text-violet-400 mx-auto" />
                    </div>

                    {/* Description */}
                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                      {member.description}
                    </p>

                    {/* Expanded Bio */}
                    <AnimatePresence>
                      {expandedTeamMember === member.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-slate-700 pt-4"
                        >
                          <p className="text-slate-400 text-xs leading-relaxed">
                            {member.expandedBio}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Expand Indicator */}
                    <div className="text-center">
                      <SafeIcon 
                        icon={FiChevronDown} 
                        className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${
                          expandedTeamMember === member.id ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* End Transition - CTA to Next Section */}
            <motion.div
              className="text-center mt-16 p-8 bg-gradient-to-r from-violet-900/30 to-blue-900/30 rounded-2xl border border-violet-400/30"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                👇 See how it actually works in action — sneak peek inside the course
              </h3>
              <p className="text-slate-300 text-lg mb-6">
                Don't just take our word for it. Watch real students building real apps.
              </p>
              <motion.button
                onClick={() => setIsTrailerOpen(true)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                Watch Course Preview
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Sneak Peek / Trailer Section */}
        <section id="sneak-peek" className="relative py-20 px-4 bg-white dark:bg-slate-900 overflow-hidden">
          {/* Animated Wave Dividers */}
          <div className="absolute top-0 left-0 w-full h-20 overflow-hidden">
            <motion.div
              className="absolute top-0 left-0 w-[200%] h-full"
              animate={{ x: currentWaveShimmer === 0 ? ['-50%', '0%'] : ['-50%', '0%'] }}
              transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
            >
              <svg viewBox="0 0 1200 120" className="w-full h-full fill-slate-100 dark:fill-slate-800">
                <path d="M0,60 C300,120 900,0 1200,60 L1200,0 L0,0 Z"></path>
              </svg>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 w-full h-20 overflow-hidden">
            <motion.div
              className="absolute bottom-0 left-0 w-[200%] h-full"
              animate={{ x: currentWaveShimmer === 1 ? ['0%', '-50%'] : ['0%', '-50%'] }}
              transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
            >
              <svg viewBox="0 0 1200 120" className="w-full h-full fill-slate-100 dark:fill-slate-800">
                <path d="M0,60 C300,0 900,120 1200,60 L1200,120 L0,120 Z"></path>
              </svg>
            </motion.div>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl font-extrabold mb-4 text-slate-900 dark:text-slate-50">
                Sneak Peek Inside the Program
              </h2>
              <p className="text-lg lg:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                See exactly how we build — live, uncut, apps that actually ship.<br />
                Choose your language below and watch the real course trailer.
              </p>
            </motion.div>

            {/* Desktop Layout: Two Columns */}
            <div className="lg:grid lg:grid-cols-3 lg:gap-12 lg:items-start">
              
              {/* Left Column: Video Player (Desktop) / Full Width (Mobile) */}
              <div className="lg:col-span-2 mb-12 lg:mb-0">
                
                {/* Language Toggle Buttons */}
                <motion.div
                  className="flex justify-center mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded-full border border-slate-200 dark:border-slate-700">
                    <div className="flex gap-2">
                      <motion.button
                        onClick={() => handleLanguageToggle('english')}
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                          selectedLanguage === 'english'
                            ? 'bg-violet-500 text-white shadow-lg'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="flex items-center gap-2">
                          🎥 Watch in English
                        </span>
                      </motion.button>
                      
                      <motion.button
                        onClick={() => handleLanguageToggle('hindi')}
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                          selectedLanguage === 'hindi'
                            ? 'bg-violet-500 text-white shadow-lg'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="flex items-center gap-2">
                          🎥 Watch in Hindi
                        </span>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>

                {/* Video Player */}
                <motion.div
                  className="relative aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-2xl overflow-hidden shadow-2xl border-2 border-violet-200 dark:border-violet-800"
                  key={selectedLanguage}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                >
                  {/* Video Placeholder */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {/* Language-specific thumbnails */}
                      <motion.div
                        className="w-32 h-32 lg:w-40 lg:h-40 bg-gradient-to-br from-violet-600 to-blue-600 rounded-2xl flex items-center justify-center text-4xl lg:text-5xl mb-6 mx-auto shadow-xl"
                        animate={{ rotateY: [0, selectedLanguage === 'english' ? 5 : -5, 0] }}
                        transition={{ duration: 0.8 }}
                      >
                        {selectedLanguage === 'english' ? '👨‍💻' : '🧑‍🏫'}
                      </motion.div>
                      
                      <h3 className="text-xl lg:text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                        {selectedLanguage === 'english' 
                          ? 'Course Trailer - English' 
                          : 'Course Trailer - Hindi'}
                      </h3>
                      
                      <p className="text-slate-600 dark:text-slate-400 mb-6">
                        {selectedLanguage === 'english'
                          ? 'See live coding sessions and real app deployments'
                          : 'लाइव कोडिंग सेशन और रियल ऐप डिप्लॉयमेंट देखें'}
                      </p>

                      {/* Play Button */}
                      <motion.button
                        onClick={() => setIsTrailerOpen(true)}
                        className="inline-flex items-center gap-3 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
                        whileHover={{ y: -2, scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        animate={{ scale: [0.98, 1.02, 0.98] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <SafeIcon icon={FiPlay} className="w-6 h-6" />
                        Play Trailer
                      </motion.button>
                    </div>
                  </div>

                  {/* Subtle border glow */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-violet-300/30 dark:border-violet-500/30 pointer-events-none"></div>
                </motion.div>
              </div>

              {/* Right Column: What You'll See Inside (Desktop) */}
              <div className="lg:sticky lg:top-8">
                <motion.div
                  className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-900/20 dark:to-blue-900/20 rounded-2xl p-8 border border-violet-200 dark:border-violet-800"
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-50 mb-6 flex items-center gap-2">
                    <SafeIcon icon={FiEye} className="w-6 h-6 text-violet-500" />
                    Inside the trailer, you'll see:
                  </h3>
                  
                  <div className="space-y-4">
                    {trailerHighlights.map((highlight, index) => (
                      <motion.div
                        key={index}
                        className="flex items-start gap-4 p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg border border-violet-100 dark:border-violet-800/50"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <p className="text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                          {highlight.text}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Featured Apps Showcase */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-900/20 dark:to-violet-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <h4 className="font-bold text-slate-900 dark:text-slate-50 mb-4 flex items-center gap-2">
                      <span className="text-xl">🚀</span>
                      Featured Real Apps:
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Annapurna Foods - Order Management System
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Kushl.app - AI-Powered Business Dashboard
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-violet-500 rounded-full"></div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          ROI Calculator - Financial Planning Tool
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* End Transition - CTA to Testimonials */}
            <motion.div
              className="text-center mt-16 p-8 bg-gradient-to-r from-green-50 to-violet-50 dark:from-green-900/20 dark:to-violet-900/20 rounded-2xl border border-green-200 dark:border-green-800"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SafeIcon icon={FiArrowDown} className="w-8 h-8 text-violet-500 mx-auto mb-4 animate-bounce" />
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                👀 Want to see what students already built?
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg mb-6">
                Real results from real people. No fake testimonials. Just shipped apps.
              </p>
              <motion.button
                onClick={scrollToTestimonials}
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
                See Student Results
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Our Promise Section */}
        <section id="promise" className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
          <div className="max-w-6xl mx-auto">
            
            {/* Desktop Layout: Two Columns */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
              
              {/* Left Column: Animated Shield/Thumbs-up (Desktop) / Top (Mobile) */}
              <motion.div
                className="mb-12 lg:mb-0 flex justify-center lg:justify-start"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative">
                  {/* Animated Shield with Thumbs Up */}
                  <motion.div
                    className="w-80 h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-violet-100 to-blue-100 dark:from-violet-900/20 dark:to-blue-900/20 rounded-full flex items-center justify-center shadow-2xl border-4 border-violet-200 dark:border-violet-800"
                    animate={{ 
                      rotate: [0, -3, 3, 0],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ 
                      duration: 5, 
                      repeat: Infinity, 
                      ease: 'easeInOut' 
                    }}
                  >
                    {/* Shield Background */}
                    <div className="w-64 h-64 lg:w-72 lg:h-72 bg-gradient-to-br from-violet-500 to-blue-500 rounded-full flex items-center justify-center relative overflow-hidden">
                      {/* Shine effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      />
                      
                      {/* Shield Icon + Thumbs Up */}
                      <div className="relative z-10 text-center">
                        <motion.div
                          className="text-8xl lg:text-9xl mb-4"
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                        >
                          🛡️
                        </motion.div>
                        <motion.div
                          className="text-4xl lg:text-5xl"
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                        >
                          👍
                        </motion.div>
                      </div>
                    </div>

                    {/* Floating elements around shield */}
                    <motion.div
                      className="absolute -top-4 -right-4 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-xl"
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      ✓
                    </motion.div>
                    
                    <motion.div
                      className="absolute -bottom-4 -left-4 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl"
                      animate={{ y: [0, 10, 0] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      ∞
                    </motion.div>
                    
                    <motion.div
                      className="absolute top-8 -left-8 w-10 h-10 bg-violet-500 rounded-full flex items-center justify-center text-white text-lg"
                      animate={{ x: [0, -5, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      💰
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right Column: Promise Content */}
              <motion.div
                className="text-center lg:text-left"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Section Title */}
                <h2 className="text-3xl lg:text-4xl font-extrabold mb-4 text-slate-900 dark:text-slate-50">
                  <span className="uppercase tracking-wide">OUR PROMISE:</span>
                  <br />
                  <span className="text-violet-600 dark:text-violet-400">Your Success, Guaranteed</span>
                </h2>

                {/* Subtext */}
                <p className="text-lg lg:text-xl text-slate-600 dark:text-slate-300 mb-12 leading-relaxed max-w-2xl">
                  We want this to be the last course you ever buy to learn AI app building. 
                  That's why we back it with a simple promise:
                </p>

                {/* Promise Perks */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                  {promisePerks.map((perk, index) => (
                    <motion.div
                      key={index}
                      className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={perk.icon} className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        
                        <div>
                          {/* Title with checkmark */}
                          <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 flex items-center gap-2">
                            <span className="text-green-600 dark:text-green-400">✅</span>
                            {perk.title}
                          </h3>
                          
                          {/* Description */}
                          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                            {perk.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <motion.div
                  className="text-center lg:text-left"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <motion.button
                    onClick={scrollToPromise}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                    whileHover={{ y: -3, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={FiShield} className="w-6 h-6" />
                    See Our Full Guarantee
                  </motion.button>
                </motion.div>
              </motion.div>
            </div>

            {/* Scrolling Benefits Ribbon */}
            <motion.div
              className="mt-20 bg-gradient-to-r from-violet-600 to-blue-600 py-4 rounded-xl overflow-hidden relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              onMouseEnter={() => setIsRibbonPaused(true)}
              onMouseLeave={() => setIsRibbonPaused(false)}
            >
              {/* Scrolling Text */}
              <motion.div
                className="flex gap-8 whitespace-nowrap text-white font-medium"
                animate={{ 
                  x: isRibbonPaused ? undefined : ['-100%', '0%'] 
                }}
                transition={{ 
                  duration: 30, 
                  repeat: Infinity, 
                  ease: 'linear',
                  ...(isRibbonPaused && { duration: 0 })
                }}
              >
                <div className="flex gap-8">
                  {[...ribbonBenefits, ...ribbonBenefits].map((benefit, index) => (
                    <span key={index} className="flex items-center gap-2">
                      <span className="text-yellow-300">•</span>
                      {benefit}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Gradient overlays for smooth edges */}
              <div className="absolute left-0 top-0 w-20 h-full bg-gradient-to-r from-violet-600 to-transparent pointer-events-none"></div>
              <div className="absolute right-0 top-0 w-20 h-full bg-gradient-to-l from-blue-600 to-transparent pointer-events-none"></div>
            </motion.div>

            {/* Transition CTA */}
            <motion.div
              className="text-center mt-16 p-8 bg-gradient-to-r from-green-50 to-violet-50 dark:from-green-900/20 dark:to-violet-900/20 rounded-2xl border border-green-200 dark:border-green-800"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4 flex items-center justify-center gap-3">
                <span className="text-3xl">💰</span>
                Ready to invest in yourself?
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg mb-6">
                See exactly what you get and how much it costs.
              </p>
              <motion.button
                onClick={scrollToPricing}
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiArrowDown} className="w-5 h-5" />
                See Pricing Below
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Trailer Modal */}
        <AnimatePresence>
          {isTrailerOpen && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTrailerOpen(false)}
            >
              <motion.div
                className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 z-10">
                  <div className="flex justify-between items-center">
                    <h3 className="text-white font-semibold">
                      AWX AI Course Trailer - {selectedLanguage === 'english' ? 'English' : 'Hindi'}
                    </h3>
                    <button
                      onClick={() => setIsTrailerOpen(false)}
                      className="text-white hover:text-slate-300 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Video Placeholder */}
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  <div className="text-center text-white">
                    <SafeIcon icon={FiPlay} className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-slate-400 text-lg mb-2">
                      {selectedLanguage === 'english' ? 'English Course Trailer' : 'Hindi Course Trailer'}
                    </p>
                    <p className="text-xs text-slate-500">
                      YouTube embed with autoplay, controls, and closed captions
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      Different thumbnail poses for each language ensure authentic switching experience
                    </p>
                  </div>
                </div>

                {/* Language Selector in Modal */}
                <div className="absolute bottom-4 left-4 right-4 flex justify-center">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full p-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleLanguageToggle('english')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          selectedLanguage === 'english'
                            ? 'bg-white text-black'
                            : 'text-white hover:bg-white/20'
                        }`}
                      >
                        English
                      </button>
                      <button
                        onClick={() => handleLanguageToggle('hindi')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          selectedLanguage === 'hindi'
                            ? 'bg-white text-black'
                            : 'text-white hover:bg-white/20'
                        }`}
                      >
                        Hindi
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default AWXLandingPage;